import * as React from "react";
import type { Session, User } from "@supabase/supabase-js";
import { quiet } from "@/lib/quiet-import";
import { supabase } from "@/integrations/supabase/client";
import { lazyToast as toast } from "@/lib/lazy-toast";
import {
  hasLocalSupabaseSession,
  isBrokeredPreviewFrame,
} from "@/lib/hasLocalSupabaseSession";
import { setAuthSnapshot } from "@/lib/auth-snapshot";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

const LINK_CHECK_KEY = "lecmo-orphan-orders-checked";
const LOCALE_STORAGE_KEY = "lux-locale-v3";
const PRESENCE_PING_KEY = "lecmo-presence-pinged-at";
const PRESENCE_PING_TTL_MS = 24 * 60 * 60 * 1000; // 24h

type ToastLocale = "ar" | "en" | "fr";

function getLocaleFromStorage(): ToastLocale {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { locale?: string };
      if (parsed.locale === "ar" || parsed.locale === "fr") return parsed.locale;
    }
  } catch {
    /* ignore */
  }
  return "en";
}

function buildToast(locale: ToastLocale, n: number) {
  if (locale === "ar") {
    const word = n === 1 ? "طلب سابق" : n === 2 ? "طلبَين سابقَين" : `${n} طلبات سابقة`;
    return {
      title: `✨ تم ربط ${word} بحسابك`,
      description: "أصبحت متاحة الآن في صفحة طلباتي.",
      action: "شاهد طلباتي",
    };
  }
  if (locale === "fr") {
    return {
      title:
        n === 1
          ? "✨ 1 commande précédente associée à votre compte"
          : `✨ ${n} commandes précédentes associées à votre compte`,
      description: "Elles sont maintenant disponibles dans Mes commandes.",
      action: "Voir mes commandes",
    };
  }
  return {
    title:
      n === 1
        ? "✨ 1 previous order linked to your account"
        : `✨ ${n} previous orders linked to your account`,
    description: "They're now available in My Orders.",
    action: "View my orders",
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;
    let bootstrapped = false;
    let receivedInitialSessionEvent = false;
    let bootstrapRetryCount = 0;
    let bootstrapRetryTimer: number | null = null;

    const mirrorSessionForNestedEditors = (nextSession: Session) => {
      if (!isBrokeredPreviewFrame()) return;
      const auth = supabase.auth as typeof supabase.auth & { storageKey?: string };
      if (!auth.storageKey) return;
      try {
        // محرّرات البلوكات iframe من نفس أصل المعاينة، لكنّها ليست ابناً مباشراً
        // لمحرّر Lovable. النسخة المحليّة تمنحها الجلسة بلا تمرير token برسائل.
        localStorage.setItem(auth.storageKey, JSON.stringify(nextSession));
      } catch {
        /* localStorage محظور */
      }
    };

    const applyAuthState = ({
      session: nextSession,
      user: nextUser,
    }: {
      session: Session | null;
      user: User | null;
    }) => {
      if (!active) return;
      setSession(nextSession);
      setUser(nextUser);
      setAuthSnapshot(nextSession);
    };

    const applySession = (nextSession: Session | null) => {
      if (nextSession?.access_token) mirrorSessionForNestedEditors(nextSession);
      applyAuthState({ session: nextSession, user: nextSession?.user ?? null });
    };

    const finishBootstrap = ({
      session: nextSession,
      user: nextUser,
    }: {
      session: Session | null;
      user: User | null;
    }) => {
      if (!active || bootstrapped) return;
      bootstrapped = true;
      applyAuthState({ session: nextSession, user: nextUser });
      setLoading(false);
    };

    const clearBootstrapRetry = () => {
      if (bootstrapRetryTimer !== null) {
        window.clearTimeout(bootstrapRetryTimer);
        bootstrapRetryTimer = null;
      }
    };

    const scheduleBootstrapRetry = () => {
      if (!active || bootstrapped) return;
      const canRecoverSession = hasLocalSupabaseSession() || isBrokeredPreviewFrame();
      if (!canRecoverSession) {
        if (receivedInitialSessionEvent) {
          finishBootstrap({ session: null, user: null });
        }
        return;
      }
      if (bootstrapRetryCount >= 8) {
        finishBootstrap({ session: null, user: null });
        return;
      }
      bootstrapRetryCount += 1;
      clearBootstrapRetry();
      bootstrapRetryTimer = window.setTimeout(() => {
        void bootstrapAuth();
      }, 500);
    };

    // CRITICAL: set up listener BEFORE getSession
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      applySession(newSession);

      // لا ننهي التحميل عند أوّل event عابر قد يحمل null أثناء استرجاع الجلسة.
      // الحسم يكون بعد bootstrap صريح يجمع getSession + getUser لتفادي false logout.
      if (event === "INITIAL_SESSION") {
        receivedInitialSessionEvent = true;
        if (newSession?.access_token) {
          clearBootstrapRetry();
          finishBootstrap({ session: newSession, user: newSession.user ?? null });
        }
      } else if (!bootstrapped && newSession?.access_token) {
        // بعض المتصفّحات/الظروف قد تسترجع الجلسة عبر حدث لاحق قبل INITIAL_SESSION.
        // إن كانت لدينا جلسة صحيحة، لا ننتظر null مؤقّتاً من fallback.
        clearBootstrapRetry();
        finishBootstrap({ session: newSession, user: newSession.user ?? null });
      } else if (bootstrapped) {
        setLoading(false);
      }

      // عند الدخول الفعليّ — افحص الطلبات اليتيمة مرّة واحدة لكلّ جلسة.
      if (event === "SIGNED_IN" && newSession?.user) {
        try {
          if (sessionStorage.getItem(LINK_CHECK_KEY)) return;
          sessionStorage.setItem(LINK_CHECK_KEY, "1");
        } catch {
          /* ignore */
        }
        // تأخير صغير حتى لا يتزامن مع redirect/render وحتى تُحفَظ الجلسة
        // في supabase client (يحتاجها الـ middleware لإرسال Authorization header)
        setTimeout(() => {
          // تأكَّد أنّ الجلسة فعلاً مُحمَّلة قبل النداء (تفادي 401 Response)
          supabase.auth.getSession().then(({ data: { session: s } }) => {
            if (!s?.access_token) return;

            // ping presence (يحدّث last_ip_country + last_seen_at) — مرّة كلّ ٢٤ ساعة
            try {
              const lastRaw = localStorage.getItem(PRESENCE_PING_KEY);
              const lastTs = lastRaw ? Number(lastRaw) : 0;
              if (!Number.isFinite(lastTs) || Date.now() - lastTs > PRESENCE_PING_TTL_MS) {
                quiet(import("@/lib/presence.functions").then(({ pingPresence }) => {
                  pingPresence().then(() => {
                    try { localStorage.setItem(PRESENCE_PING_KEY, String(Date.now())); } catch { /* ignore */ }
                  }).catch((e: Error) => console.warn("[auth] pingPresence failed:", e));
                }));
              }
            } catch { /* ignore */ }

            // غلاف صلب: حتى لو رمى createServerFn synchronously بـ Response،
            // لا نسمح للاستثناء بالتسرّب لـ ErrorBoundary وإفراغ الشاشة.
            const safeCall = (async () => {
              try {
                const { linkOrphanOrders } = await import("@/utils/orders.functions");
                return await linkOrphanOrders();
              } catch (err) {
                if (err instanceof Response) {
                  console.warn("[auth] linkOrphanOrders skipped:", err.status);
                } else {
                  console.error("[auth] linkOrphanOrders failed:", err);
                }
                return null;
              }
            })();
            safeCall.then((res) => {
              if (!res || !res.linked || res.linked === 0) return;
              const locale = getLocaleFromStorage();
              const t = buildToast(locale, res.linked);
              toast.success(t.title, {
                description: t.description,
                duration: 8000,
                action: {
                  label: t.action,
                  onClick: () => {
                    window.location.href = "/account/orders";
                  },
                },
              });
            });
          }).catch((err) => {
            console.warn("[auth] getSession failed:", err);
          });
        }, 1000);
      }

      // عند تسجيل الخروج — اسمح بإعادة الفحص في الجلسة القادمة.
      if (event === "SIGNED_OUT") {
        try {
          sessionStorage.removeItem(LINK_CHECK_KEY);
        } catch {
          /* ignore */
        }
      }
    });

    const bootstrapAuth = async () => {
      try {
        const sessionResult = await supabase.auth.getSession();
        const initialSession = sessionResult.data.session;

        if (initialSession?.access_token) {
          mirrorSessionForNestedEditors(initialSession);
          clearBootstrapRetry();
          finishBootstrap({
            session: initialSession,
            user: initialSession.user ?? null,
          });
          return;
        }

        // السبب الجذري: أثناء reload من Vite قد تبقى الجلسة محفوظة في localStorage
        // لكن getSession تُرجع null مؤقّتاً قبل اكتمال hydration. هنا لا نعلن logout.
        if (hasLocalSupabaseSession() || isBrokeredPreviewFrame()) {
          scheduleBootstrapRetry();
          return;
        }

        if (receivedInitialSessionEvent) {
          finishBootstrap({ session: null, user: null });
        }
      } catch {
        if (hasLocalSupabaseSession() || isBrokeredPreviewFrame()) {
          scheduleBootstrapRetry();
          return;
        }
        if (receivedInitialSessionEvent) {
          finishBootstrap({ session: null, user: null });
        }
      }
    };

    void bootstrapAuth();

    // حماية احتياطيّة: نعيد probe كامل بدل الحسم المبكّر بـ null.
    const failsafe = setTimeout(() => {
      if (bootstrapped) return;
      if (hasLocalSupabaseSession() || isBrokeredPreviewFrame()) {
        scheduleBootstrapRetry();
        return;
      }
      void bootstrapAuth();
    }, 4000);

    return () => {
      active = false;
      clearTimeout(failsafe);
      clearBootstrapRetry();
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = React.useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = React.useMemo(
    () => ({ user, session, loading, signOut }),
    [user, session, loading, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * قراءة آمنة للجلسة: تُرجع حالة محيّدة بدل رمي خطأ عندما يُركَّب المكوّن
 * خارج AuthProvider (مثل مكوّنات الجذر العامّة أثناء SSR).
 * تُستعمل في hooks عامّة لا يجوز أن تُسقط الشجرة كلّها.
 */
const NO_AUTH: AuthContextValue = {
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
};

export function useAuthOptional(): AuthContextValue {
  return React.useContext(AuthContext) ?? NO_AUTH;
}

